import {
    GroupType,
    IssueMachineHealthResponse,
    MetricTypeCodes,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    loadGroupBinds,
    loadMetricBinds,
} from 'custom_hooks/data_hooks/activity/useActivityEventBinds';
import { subDays } from 'date-fns';
import { getChartMeta } from 'pages/Desk/issues/chart_data/shared';
import { clearURLQueryParams } from 'pages/workbench/shared';
import { useWorkbenchValues } from 'pages/workbench/state';
import { useCallback } from 'react';
import { hierarchyContainsGroupId } from 'utils/group_hierarchy';
import { promiseAllBatched } from 'utils/promise';
import { TrendQueryParams, WorkbenchTrendUrlAction } from 'utils/RouteHelper';

const TreeItemQueryBatchSize = 10;

export function useLoadWorkbenchStateFromParams({ systemId }: { systemId: string }) {
    const { showToast } = useNotificationContext();

    const {
        setTimeRange,
        setIndicatorTypes,
        updateFromSession,
        updateFromIndicator,
        updateFromAlert,
        updateFromActivityEvent,
        updateFromTreeItem,
        updateFromIssue,
        updateFromChartData,
        setPendingAcknowledgeAsset,
    } = useWorkbenchValues((state) => ({
        setTimeRange: state.setTimeRange,
        setIndicatorTypes: state.setIndicatorTypes,
        updateFromSession: state.updateFromSession,
        updateFromIndicator: state.updateFromIndicator,
        updateFromAlert: state.updateFromAlert,
        updateFromActivityEvent: state.updateFromActivityEvent,
        updateFromTreeItem: state.updateFromTreeItem,
        updateFromIssue: state.updateFromIssue,
        updateFromChartData: state.updateFromChartData,
        setPendingAcknowledgeAsset: state.setPendingAcknowledgeAsset,
    }));

    const { mutateAsync: loadSession } = useMutation({
        mutationFn: async (sessionId: string) => {
            const session = await sdClient.getSession({ id: sessionId });

            if (session.SystemId === systemId) {
                const treeItemQueries = session.GroupIds.map(
                    (groupId) => () => sdClient.getTreeItem({ treeItemId: groupId }),
                );
                const treeItems = await promiseAllBatched(
                    treeItemQueries,
                    TreeItemQueryBatchSize,
                );

                updateFromSession(session, treeItems);
            }
        },

        onSuccess: clearURLQueryParams,

        onError: () => showToast({ type: 'error', content: 'Failed to load session' }),
    });

    const { mutateAsync: loadIndicator } = useMutation({
        mutationFn: async (indicatorId: string) => {
            const indicator = await sdClient.getIndicator({ indicatorId });
            const isValid = hierarchyContainsGroupId(indicator.Hierarchy, systemId);

            if (isValid) {
                updateFromIndicator(indicator);
            }
        },

        onSuccess: clearURLQueryParams,

        onError: () => showToast({ type: 'error', content: 'Failed to load indicator' }),
    });

    const { mutateAsync: loadGroup } = useMutation({
        mutationFn: async (groupId: string) => {
            const treeItem = await sdClient.getTreeItem({ treeItemId: groupId });
            const isValid =
                treeItem.Id === systemId ||
                hierarchyContainsGroupId(treeItem.Hierarchy, systemId);
            if (isValid) {
                updateFromTreeItem(treeItem);
            }
        },

        onSuccess: clearURLQueryParams,
        onError: () => showToast({ type: 'error', content: 'Failed to load group' }),
    });

    const { mutateAsync: loadIssue } = useMutation({
        mutationFn: async (issueId: string) => {
            const issue = await sdClient.getIssue({ id: issueId });
            const isValid =
                issue instanceof IssueMachineHealthResponse &&
                (issue.Group?.GroupId === systemId ||
                    hierarchyContainsGroupId(issue.Hierarchy, systemId));
            if (isValid) {
                updateFromIssue(issue);
            }
        },

        onSuccess: clearURLQueryParams,
        onError: () => showToast({ type: 'error', content: 'Failed to load issue' }),
    });

    const { mutateAsync: loadChartData } = useMutation({
        mutationFn: async ({
            issueId,
            chartDataId,
        }: {
            issueId: string;
            chartDataId: string;
        }) => {
            const chartData = await sdClient.getIssueChartData({ id: chartDataId });
            const chartMeta = await getChartMeta(chartData.ChartData);

            const issue = await sdClient.getIssue({ id: issueId });
            const isValid =
                issue instanceof IssueMachineHealthResponse &&
                (issue.Group?.GroupId === systemId ||
                    hierarchyContainsGroupId(issue.Hierarchy, systemId));

            if (isValid) {
                updateFromChartData(issue, chartMeta);
            }
        },

        onSuccess: clearURLQueryParams,

        onError: () => showToast({ type: 'error', content: 'Failed to load chart data' }),
    });

    const { mutateAsync: loadAlert } = useMutation({
        mutationFn: async (alertId: string) => {
            const alert = await sdClient.getAlert({ alertId });
            const isValid = hierarchyContainsGroupId(alert.Hierarchy, systemId);

            if (isValid) {
                updateFromAlert(alert);
            }
        },

        onSuccess: clearURLQueryParams,
        onError: () => showToast({ type: 'error', content: 'Failed to load alert' }),
    });

    const { mutateAsync: loadAcknowledge } = useMutation({
        mutationFn: async (alertId: string) => {
            const alert = await sdClient.getAlert({ alertId });
            const isValid = hierarchyContainsGroupId(alert.Hierarchy, systemId);

            if (!isValid) {
                showToast({
                    type: 'error',
                    content: "This alert isn't on the current system.",
                });
                return;
            }

            updateFromAlert(alert);

            const assetId = alert.Hierarchy.find(
                ({ HierarchyType }) => HierarchyType === GroupType.Asset,
            )?.HierarchyId;

            if (!assetId) {
                showToast({
                    type: 'error',
                    content: "Couldn't find the asset for this alert.",
                });
                return;
            }

            const asset = await sdClient.getAsset({ assetId });
            setPendingAcknowledgeAsset(asset);
        },

        onSuccess: clearURLQueryParams,
        onError: () =>
            showToast({ type: 'error', content: 'Failed to open acknowledge dialog' }),
    });

    const { mutateAsync: loadActivityEvent } = useMutation({
        mutationFn: async (activityEventId: string) => {
            const activityEvent = await sdClient.getActivityEvent({ activityEventId });
            const treeItems = await loadGroupBinds(activityEvent.Binds);
            const indicators = await loadMetricBinds(activityEvent.Binds);

            const isValid =
                treeItems.every(({ Hierarchy }) =>
                    hierarchyContainsGroupId(Hierarchy, systemId),
                ) &&
                indicators.every(({ Hierarchy }) =>
                    hierarchyContainsGroupId(Hierarchy, systemId),
                );

            if (isValid) {
                updateFromActivityEvent(activityEvent, treeItems, indicators);
            }
        },

        onSuccess: clearURLQueryParams,

        onError: () =>
            showToast({ type: 'error', content: 'Failed to load activity event' }),
    });

    const loadFromQueryParams = useCallback(
        async (params: TrendQueryParams) => {
            // Clear before routing so only an explicit `?action=acknowledge` re-opens the modal.
            setPendingAcknowledgeAsset(undefined);

            await (() => {
                if (params.sessionId) {
                    return loadSession(params.sessionId);
                }
                if (params.alertId) {
                    if (params.action === WorkbenchTrendUrlAction.Acknowledge) {
                        return loadAcknowledge(params.alertId);
                    }
                    return loadAlert(params.alertId);
                }
                if (params.activityEventId) {
                    return loadActivityEvent(params.activityEventId);
                }
                if (params.indicatorId) {
                    return loadIndicator(params.indicatorId);
                }
                if (params.groupId) {
                    return loadGroup(params.groupId);
                }
                if (params.chartDataId) {
                    if (!params.issueId) {
                        showToast({
                            type: 'error',
                            content:
                                'Invalid URL. Please include an issueId in the URL when attempting to navigate to chart data.',
                        });

                        return Promise.resolve();
                    }

                    return loadChartData({
                        issueId: params.issueId,
                        chartDataId: params.chartDataId,
                    });
                }
                if (params.issueId) {
                    return loadIssue(params.issueId);
                }
                return Promise.resolve();
            })();

            if (params.startDate) {
                setTimeRange({
                    id: 'custom',
                    type: 'custom',
                    startTimestamp: subDays(new Date(params.startDate), 1).valueOf(),
                    endTimestamp: new Date().valueOf(),
                });
            }

            if (params.indicatorTypes) {
                const validCodes = new Set(Object.values(MetricTypeCodes));
                const types = params.indicatorTypes
                    .split(',')
                    .filter((code): code is MetricTypeCodes =>
                        validCodes.has(code as MetricTypeCodes),
                    );
                if (types.length > 0) {
                    setIndicatorTypes(types);
                }
            }
        },
        [
            loadSession,
            loadAlert,
            loadAcknowledge,
            loadActivityEvent,
            loadIndicator,
            loadGroup,
            loadChartData,
            showToast,
            loadIssue,
            setTimeRange,
            setIndicatorTypes,
            setPendingAcknowledgeAsset,
        ],
    );

    return { loadFromQueryParams };
}
